/**
 * CreateTeamObjectiveDialog Component
 *
 * Dialog for creating a new team objective linked to an org objective
 */

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Autocomplete,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { TrendingUp, Lock } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

export const CreateTeamObjectiveDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { orgObjectives, departments, setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, formState, updateFormField, closeCreateTeamObjDialog } = dialogs;

    const selectedDept = departments.find((d: any) => (d.id ?? d.dept_id) === formState.selectedDeptForTeamOKR);
    const [selectedOrgObjective, setSelectedOrgObjective] = useState<any>(null);

    const unlockedOrgObjectives = orgObjectives.filter((o: any) => o.is_locked !== 1);

    // Auto-select if only one unlocked org objective exists
    useEffect(() => {
        if (dialogState.createTeamObjDialog) {
            if (unlockedOrgObjectives.length === 1) {
                setSelectedOrgObjective(unlockedOrgObjectives[0]);
            } else {
                setSelectedOrgObjective(null);
            }
        }
    }, [dialogState.createTeamObjDialog, orgObjectives]);

    const handleCreate = async () => {
        if (!formState.newTeamObjTitle.trim() || !selectedOrgObjective?.id || formState.selectedDeptForTeamOKR === null || formState.selectedDeptForTeamOKR === undefined) {
            setError('Missing required fields: title, org objective, or department');
            return;
        }

        try {
            await okrApi.createTeamObjective(
                selectedOrgObjective.id,
                formState.selectedDeptForTeamOKR,
                formState.newTeamObjTitle,
                formState.newTeamObjDescription,
                25 // Default weight
            );
            setSuccess('Team objective created successfully');
            setSelectedOrgObjective(null);
            closeCreateTeamObjDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to create team objective');
        }
    };

    const handleClose = () => {
        setSelectedOrgObjective(null);
        closeCreateTeamObjDialog();
    };

    return (
        <Dialog
            open={dialogState.createTeamObjDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                    minHeight: '420px',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <TrendingUp sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Create Team Objective
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            For: {selectedDept?.name || selectedDept?.dept_name || 'Selected Department'}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Org Objective Selector */}
                    {orgObjectives.length > 1 ? (
                        <Autocomplete
                            options={orgObjectives}
                            getOptionLabel={(option) => option.title ? `${option.title}${option.is_locked === 1 ? ' (Locked)' : ''}` : ''}
                            getOptionDisabled={(option) => option.is_locked === 1}
                            value={selectedOrgObjective}
                            onChange={(_, newValue) => setSelectedOrgObjective(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Link to Organization Objective"
                                    placeholder="Select an org objective..."
                                    helperText={unlockedOrgObjectives.length === 0 ? 'All goals are locked. Unlock a goal to create team objectives.' : undefined}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props as any;
                                const locked = option.is_locked === 1;
                                return (
                                    <li key={key} {...rest} style={{ ...((rest as any).style), opacity: locked ? 0.5 : 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                                    {option.title}
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {locked && (
                                                <Lock sx={{ fontSize: 16, color: '#fb923c' }} />
                                            )}
                                        </Box>
                                    </li>
                                );
                            }}
                        />
                    ) : selectedOrgObjective ? (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: selectedOrgObjective.is_locked === 1
                                    ? (isDarkMode ? 'rgba(251, 146, 60, 0.1)' : '#fff7ed')
                                    : (isDarkMode ? '#334155' : '#f1f5f9'),
                                border: `1px solid ${selectedOrgObjective.is_locked === 1
                                    ? 'rgba(251, 146, 60, 0.3)'
                                    : (isDarkMode ? '#475569' : '#e2e8f0')}`,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                                Linked to Organization Objective:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography sx={{ fontWeight: 600 }}>
                                    {selectedOrgObjective.title}
                                </Typography>
                                {selectedOrgObjective.is_locked === 1 && (
                                    <Lock sx={{ fontSize: 16, color: '#fb923c' }} />
                                )}
                            </Box>
                            {selectedOrgObjective.is_locked === 1 && (
                                <Typography variant="caption" sx={{ color: '#fb923c', fontWeight: 600 }}>
                                    This goal is locked. Unlock it to create team objectives.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: isDarkMode ? '#334155' : '#fff7ed',
                                border: `1px solid ${isDarkMode ? '#475569' : '#fed7aa'}`,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                                No organization objectives found. Create an org objective first.
                            </Typography>
                        </Box>
                    )}

                    <TextField
                        autoFocus
                        label="Objective Title"
                        fullWidth
                        value={formState.newTeamObjTitle}
                        onChange={(e) => updateFormField('newTeamObjTitle', e.target.value)}
                        placeholder="e.g., Deliver feature X with 95% test coverage"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.newTeamObjDescription}
                        onChange={(e) => updateFormField('newTeamObjDescription', e.target.value)}
                        placeholder="Describe the team objective and key results..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!formState.newTeamObjTitle.trim() || !selectedOrgObjective?.id || selectedOrgObjective?.is_locked === 1}
                    sx={{
                        background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    Create Objective
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTeamObjectiveDialog;
